// ═══════════════════════════════════════════════════════════════════════════════
// ElementPreviewDrawer — render ONE briefing element against a chosen patient
// ═══════════════════════════════════════════════════════════════════════════════
//
// PURPOSE
//   Debug drawer for the Studio Element-Catalog "Preview" button. Side-by-side
//   compare: the catalog's canonical sample (gray, left) vs the live render
//   for the chosen patient (green, right). Diff is obvious at a glance.
//
// SHARED FROM DAY ONE
//   Lives in _Shared.Lib so both Agent Studio (talks to RestApi.Tenant) and
//   the patient/AGUI app (talks to RestApi.AGUI) can host it. The host passes
//   a `loadPreview(elementId, memberId) => Promise<ElementPreviewResult>`
//   binding so the drawer is transport-agnostic.
//
// SESSION-FIRST PATIENT
//   When the host knows the patient (Studio's patient context, or the
//   patient-app session), it passes `patient`. The drawer auto-renders on
//   open. If the host passes nothing, the drawer shows an empty state with
//   a "Pick a patient" CTA. `allowChangePatient = false` hides the Change
//   button (used in the patient app where the patient is locked).
//
// PHI POSTURE
//   The rendered text is PHI. Drawer displays it for review and never
//   persists. The patient-bar pill shows initials + masked MRN only.
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import { Button, Drawer, Tag, Tooltip, message } from 'antd';
import {
  CloseOutlined,
  CopyOutlined,
  ExperimentOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { PatientPicker } from './PatientPicker';
import type {
  ElementPreviewResult,
  MemberSearchHit,
  PatientHandle,
} from './types';

export interface ElementPreviewDrawerProps {
  open: boolean;
  onClose: () => void;

  /** Element to preview. */
  element: {
    id: number;
    code: string;
    displayName?: string;
    category?: string;
    tier: number;
    /** Catalog SampleOutput — drawer falls back to host-provided text when missing. */
    sampleOutput?: string | null;
  };

  /** Session patient — pre-fills the drawer's patient pill. */
  patient?: PatientHandle;

  /** Render the element against memberId. Host owns the transport. */
  loadPreview: (elementId: number, memberId: number) => Promise<ElementPreviewResult>;

  /** Member search backing the Change-patient popover. */
  searchPatients?: (query: string) => Promise<MemberSearchHit[]>;

  /** False in the patient app (patient is locked to session). Defaults to true. */
  allowChangePatient?: boolean;
}

// Lifecycle of the drawer body. A "needs-patient" empty state appears only
// when the host opens us without `patient` and the user hasn't picked yet.
type Stage = 'needs-patient' | 'rendering' | 'loaded' | 'error';

export function ElementPreviewDrawer(props: ElementPreviewDrawerProps) {
  const { open, onClose, element, patient, loadPreview, searchPatients } = props;
  const allowChangePatient = props.allowChangePatient ?? true;

  const [stage, setStage]               = useState<Stage>('needs-patient');
  const [errorMsg, setErrorMsg]         = useState<string | null>(null);
  const [result, setResult]             = useState<ElementPreviewResult | null>(null);
  const [activePatient, setActivePatient] = useState<PatientHandle | undefined>(patient);
  const [pickerOpen, setPickerOpen]     = useState(false);

  // Reset + auto-render on open. The activePatient seed comes from the host
  // prop; if there's no host-provided patient, the empty state takes over.
  useEffect(() => {
    if (!open) return;
    setErrorMsg(null);
    setResult(null);
    setPickerOpen(false);
    setActivePatient(patient);
    setStage(patient ? 'rendering' : 'needs-patient');
  }, [open, patient]);

  // Fire the load whenever stage flips to 'rendering' and we have a patient.
  // Two-effect split (vs putting the load inside the open-effect above) makes
  // the "Re-render" + "Change patient" flows trivial — they just set stage.
  useEffect(() => {
    if (stage !== 'rendering' || !activePatient) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await loadPreview(element.id, activePatient.memberId);
        if (cancelled) return;
        setResult(data);
        setStage('loaded');
      } catch (e: unknown) {
        if (cancelled) return;
        setErrorMsg(e instanceof Error ? e.message : 'Preview failed.');
        setStage('error');
      }
    })();
    return () => { cancelled = true; };
  }, [stage, activePatient, element.id, loadPreview]);

  const handlePick = useCallback((hit: MemberSearchHit) => {
    const label = [hit.firstName, hit.lastName].filter(Boolean).join(' ') || `Member #${hit.id}`;
    const meta  = formatMemberMeta(hit);
    setActivePatient({ memberId: hit.id, label, meta });
    setPickerOpen(false);
    setStage('rendering');
  }, []);

  const handleRerender = useCallback(() => {
    if (!activePatient) return;
    setStage('rendering');
  }, [activePatient]);

  const handleCopy = useCallback(async () => {
    if (!result?.text) return;
    try {
      await navigator.clipboard.writeText(result.text);
      message.success('Copied live output');
    } catch {
      message.error('Copy failed');
    }
  }, [result]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width={760}
      closable={false}
      title={null}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        header: { display: 'none' },
      }}
    >
      {/* DEBUG banner */}
      <div style={debugBannerStyle}>
        <span style={debugDotStyle} />
        DEBUG PREVIEW · uses real patient data · renders identically to the LLM
      </div>

      {/* Header */}
      <div style={{ padding: '18px 24px 14px', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={iconTileStyle}><ExperimentOutlined /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: 19, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
            {element.displayName || element.code}
          </h1>
          <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={codeStyle}>{element.code}</span>
            {element.category && <Tag style={{ ...tagStyle, ...catTagStyle }}>{element.category}</Tag>}
            <Tag style={{ ...tagStyle, ...tierTagStyle }}>{`Tier ${element.tier} · ${element.tier === 1 ? 'Stable' : 'Per-turn'}`}</Tag>
          </div>
        </div>
        <Button type="text" icon={<CloseOutlined />} onClick={onClose} />
      </div>

      {/* Patient bar */}
      {activePatient && (
        <div style={ptBarStyle}>
          <div style={avatarStyle}>{patientInitials(activePatient)}</div>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>{activePatient.label || `Member #${activePatient.memberId}`}</span>
              <Tooltip title="Inherited from the patient currently selected in this app">
                <Tag style={fromSessionPillStyle}>↳ from session</Tag>
              </Tooltip>
            </div>
            {activePatient.meta && (
              <div style={{ color: '#94A3B8', fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }}>
                {activePatient.meta}
              </div>
            )}
          </div>
          <div style={{ flex: 1 }} />
          {allowChangePatient && searchPatients && (
            <Button size="small" icon={<UserOutlined />} onClick={() => setPickerOpen((v) => !v)}>
              {pickerOpen ? 'Hide' : 'Change patient'}
            </Button>
          )}
        </div>
      )}

      {/* Expanded patient picker (toggled by Change button or shown when no patient) */}
      {(pickerOpen || stage === 'needs-patient') && searchPatients && (
        <div style={{ padding: '0 24px 4px', marginTop: 12 }}>
          <PatientPicker
            searchPatients={searchPatients}
            onPick={handlePick}
            currentMemberId={activePatient?.memberId}
          />
        </div>
      )}

      {/* Body */}
      <div style={bodyStyle}>
        {stage === 'needs-patient' && !pickerOpen && (
          <EmptyState
            hasPicker={Boolean(searchPatients)}
            onPickClick={() => setPickerOpen(true)}
            canonicalSample={element.sampleOutput}
          />
        )}

        {stage === 'rendering' && (
          <CompareGrid
            canonicalSample={element.sampleOutput}
            liveText="Rendering…"
            liveStatus="loading"
            elapsedMs={null}
          />
        )}

        {stage === 'error' && (
          <div style={errorBoxStyle}>
            {errorMsg || 'Preview failed.'}
            <div style={{ marginTop: 10 }}>
              <Button size="small" icon={<ReloadOutlined />} onClick={handleRerender} disabled={!activePatient}>
                Try again
              </Button>
            </div>
          </div>
        )}

        {stage === 'loaded' && result && (
          <>
            <CompareGrid
              canonicalSample={element.sampleOutput ?? result.canonicalSample}
              liveText={result.text}
              liveStatus={result.status}
              elapsedMs={result.elapsedMs}
              faultMessage={result.faultMessage}
            />

            {/* Provider details */}
            <SectionLabel>Provider details</SectionLabel>
            <div style={detailsBoxStyle}>
              <DetailRow k="Renderer class" v={result.rendererClass || '—'} />
              <DetailRow k="Element code"   v={result.elementCode} />
              <DetailRow k="Category · tier" v={`${result.category || '—'} · Tier ${result.tier} ${result.tier === 1 ? '(cached across conversation)' : '(per-turn)'}`} muted />
              <DetailRow k="Max tokens"     v={String(result.maxTokens)} />
            </div>

            {/* Raw payload */}
            <SectionLabel style={{ marginTop: 18 }}>Raw debug payload</SectionLabel>
            <details style={detailsBlockStyle}>
              <summary style={{ cursor: 'pointer', padding: '11px 14px', fontSize: 12, color: '#475569' }}>
                ElementPreviewResult DTO
              </summary>
              <pre style={preStyle}>{JSON.stringify(result, null, 2)}</pre>
            </details>
          </>
        )}
      </div>

      {/* Footer */}
      <div style={footerStyle}>
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          onClick={handleRerender}
          disabled={!activePatient || stage === 'rendering'}
        >
          Re-render
        </Button>
        <Button
          icon={<CopyOutlined />}
          onClick={handleCopy}
          disabled={stage !== 'loaded' || !result?.text}
        >
          Copy live output
        </Button>
        <div style={{ flex: 1 }} />
        <Button type="text" onClick={onClose}>Close</Button>
      </div>
    </Drawer>
  );
}

// ────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────

function EmptyState({
  hasPicker, onPickClick, canonicalSample,
}: {
  hasPicker: boolean;
  onPickClick: () => void;
  canonicalSample?: string | null;
}) {
  return (
    <div>
      <div style={emptyStateStyle}>
        <div style={{ fontSize: 24, marginBottom: 6 }}><UserOutlined /></div>
        <div style={{ fontSize: 14, fontWeight: 500, color: '#0F172A', marginBottom: 4 }}>
          No patient selected
        </div>
        <div style={{ fontSize: 12, color: '#475569', marginBottom: 14, maxWidth: 340 }}>
          Pick a patient to render this element. The canonical sample below stays available for reference.
        </div>
        {hasPicker && (
          <Button type="primary" icon={<UserOutlined />} onClick={onPickClick}>
            Pick a patient
          </Button>
        )}
      </div>

      {canonicalSample && (
        <>
          <SectionLabel style={{ marginTop: 22 }}>Canonical sample</SectionLabel>
          <div style={{ ...compareCellStyle, ...canonicalCellStyle }}>{canonicalSample}</div>
        </>
      )}
    </div>
  );
}

function CompareGrid({
  canonicalSample, liveText, liveStatus, elapsedMs, faultMessage,
}: {
  canonicalSample?: string | null;
  liveText?: string | null;
  liveStatus: string;
  elapsedMs: number | null;
  faultMessage?: string | null;
}) {
  const isError   = liveStatus === 'Fault'   || liveStatus === 'NoProvider';
  const isLoading = liveStatus === 'loading';
  const isOK      = liveStatus === 'Rendered';

  return (
    <>
      <SectionLabel>Output · canonical reference vs live render</SectionLabel>
      <div style={compareGridStyle}>
        {/* Canonical */}
        <div>
          <div style={compareHeadStyle}>
            <span style={compareHeadTitleStyle}>Canonical sample</span>
            <span style={compareHeadAsideStyle}>catalog</span>
          </div>
          <div style={{ ...compareCellStyle, ...canonicalCellStyle, borderRight: '1px solid #E5E7EB' }}>
            {canonicalSample || <span style={{ color: '#94A3B8', fontStyle: 'italic' }}>(no sample on this row)</span>}
          </div>
        </div>

        {/* Live */}
        <div>
          <div style={{ ...compareHeadStyle, ...(isOK ? liveHeadOkStyle : isError ? liveHeadErrStyle : isLoading ? liveHeadLoadingStyle : {}) }}>
            <span style={{ ...compareHeadTitleStyle, color: isOK ? '#064E3B' : isError ? '#7F1D1D' : undefined }}>
              {isOK ? '✓ Live · rendered' : isError ? `⚠ Live · ${liveStatus.toLowerCase()}` : isLoading ? 'Live · rendering…' : `Live · ${liveStatus.toLowerCase()}`}
            </span>
            <span style={compareHeadAsideStyle}>
              {elapsedMs == null ? '—' : `${Math.round(elapsedMs)} ms`}
            </span>
          </div>
          <div style={{ ...compareCellStyle, ...(isOK ? liveCellOkStyle : isError ? liveCellErrStyle : {}) }}>
            {isError && faultMessage
              ? <span style={{ color: '#7F1D1D' }}>{faultMessage}</span>
              : (liveText || <span style={{ color: '#94A3B8', fontStyle: 'italic' }}>(no text)</span>)}
          </div>
        </div>
      </div>
    </>
  );
}

function SectionLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94A3B8', fontWeight: 700, margin: '14px 0 8px', ...style }}>
      {children}
    </div>
  );
}

function DetailRow({ k, v, muted }: { k: string; v: string; muted?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: '1px solid #EEF2F7', fontSize: 12.5 }}>
      <span style={{ color: '#94A3B8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, width: 140, flexShrink: 0 }}>{k}</span>
      <span style={{ color: '#0F172A', fontFamily: muted ? 'inherit' : 'JetBrains Mono, monospace', flex: 1, wordBreak: 'break-word' }}>{v}</span>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Small helpers
// ────────────────────────────────────────────────────────────────────

function patientInitials(p: PatientHandle): string {
  if (p.initials) return p.initials;
  const label = p.label || '';
  const parts = label.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (label.length >= 2) return label.slice(0, 2).toUpperCase();
  return '?';
}

function formatMemberMeta(hit: MemberSearchHit): string {
  const age = hit.dob ? ageFromDob(hit.dob) : null;
  const sex = (hit.gender || '').trim().toLowerCase().startsWith('m') ? 'M' : (hit.gender || '').trim().toLowerCase().startsWith('f') ? 'F' : '';
  const parts: string[] = [];
  if (age != null) parts.push(`${age}${sex}`);
  if (hit.mrn) parts.push(`MRN ••••${hit.mrn.slice(-4)}`);
  if (hit.languageCode) parts.push(hit.languageCode);
  return parts.join(' · ');
}

function ageFromDob(dob: string): number | null {
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age >= 0 ? age : null;
}

// ────────────────────────────────────────────────────────────────────
// Styles (inline so the shared lib has no theme coupling)
// ────────────────────────────────────────────────────────────────────

const debugBannerStyle: React.CSSProperties = {
  background: 'linear-gradient(90deg,#FEF3C7 0%, #FDE68A 100%)',
  color: '#78350F', fontSize: 11, fontWeight: 600, letterSpacing: '0.02em',
  padding: '7px 22px', display: 'flex', alignItems: 'center', gap: 8,
  borderBottom: '1px solid #FCD34D',
};
const debugDotStyle: React.CSSProperties = {
  width: 6, height: 6, borderRadius: '50%',
  background: '#D97706', boxShadow: '0 0 0 3px rgba(217,119,6,.18)',
};

const iconTileStyle: React.CSSProperties = {
  width: 36, height: 36, borderRadius: 10,
  background: '#EDE9FE', color: '#4C1D95',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0, fontWeight: 700, fontSize: 16,
};

const codeStyle: React.CSSProperties = {
  fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: '#94A3B8',
};

const tagStyle: React.CSSProperties = {
  margin: 0, fontSize: 10, fontWeight: 600, letterSpacing: '0.02em', textTransform: 'uppercase',
  padding: '1px 9px', borderRadius: 999, lineHeight: '18px',
};
const catTagStyle: React.CSSProperties  = { background: '#EDE9FE', color: '#4C1D95', borderColor: '#C4B5FD' };
const tierTagStyle: React.CSSProperties = { background: '#DBEAFE', color: '#1E3A5F', borderColor: '#BFDBFE' };

const ptBarStyle: React.CSSProperties = {
  margin: '0 24px', padding: '10px 14px', background: '#FAFBFD',
  border: '1px solid #E5E7EB', borderRadius: 10,
  display: 'flex', alignItems: 'center', gap: 10,
};
const avatarStyle: React.CSSProperties = {
  width: 30, height: 30, borderRadius: '50%',
  background: 'linear-gradient(135deg,#FB923C,#F97316)', color: '#fff',
  fontWeight: 600, fontSize: 12,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0, boxShadow: '0 0 0 2px #FED7AA',
};
const fromSessionPillStyle: React.CSSProperties = {
  background: '#ECFEFF', color: '#155E75', borderColor: '#A5F3FC',
  margin: 0, fontSize: 9.5, fontWeight: 500, padding: '1px 7px', borderRadius: 999,
  textTransform: 'none', letterSpacing: 0,
};

const bodyStyle: React.CSSProperties = {
  flex: 1, overflow: 'auto', padding: '6px 24px 100px',
};

const compareGridStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0,
  background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden',
};
const compareHeadStyle: React.CSSProperties = {
  padding: '10px 14px 8px', background: '#FAFBFD',
  borderBottom: '1px solid #E5E7EB',
  display: 'flex', alignItems: 'center', gap: 6,
};
const compareHeadTitleStyle: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#475569',
};
const compareHeadAsideStyle: React.CSSProperties = {
  marginLeft: 'auto', fontSize: 10.5, color: '#94A3B8', fontFamily: 'JetBrains Mono, monospace',
};
const liveHeadOkStyle: React.CSSProperties  = { background: '#ECFDF5', borderBottomColor: '#A7F3D0' };
const liveHeadErrStyle: React.CSSProperties = { background: '#FEF2F2', borderBottomColor: '#FCA5A5' };
const liveHeadLoadingStyle: React.CSSProperties = { background: '#EFF6FF', borderBottomColor: '#BFDBFE' };

const compareCellStyle: React.CSSProperties = {
  padding: 14, fontFamily: 'JetBrains Mono, monospace',
  fontSize: 13, lineHeight: 1.55, color: '#0F172A', minHeight: 64,
  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
};
const canonicalCellStyle: React.CSSProperties = { color: '#475569' };
const liveCellOkStyle: React.CSSProperties    = { background: '#F0FDF4' };
const liveCellErrStyle: React.CSSProperties   = { background: '#FEF2F2' };

const detailsBoxStyle: React.CSSProperties = {
  background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: '4px 14px',
};

const detailsBlockStyle: React.CSSProperties = {
  background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden',
};
const preStyle: React.CSSProperties = {
  margin: 0, padding: '14px 16px', fontFamily: 'JetBrains Mono, monospace',
  fontSize: 11.5, lineHeight: 1.55, background: '#0F172A', color: '#E2E8F0', overflow: 'auto',
};

const emptyStateStyle: React.CSSProperties = {
  padding: 28, border: '1px dashed #BFDBFE', background: '#F0F7FF',
  borderRadius: 12, textAlign: 'center',
  display: 'flex', flexDirection: 'column', alignItems: 'center',
};

const errorBoxStyle: React.CSSProperties = {
  padding: 14, background: '#FEF2F2', color: '#7F1D1D',
  border: '1px solid #FCA5A5', borderRadius: 10, fontSize: 13,
};

const footerStyle: React.CSSProperties = {
  position: 'sticky', bottom: 0, background: '#fff',
  borderTop: '1px solid #E5E7EB', padding: '12px 22px',
  display: 'flex', alignItems: 'center', gap: 8,
  boxShadow: '0 -6px 16px rgba(15,23,42,.04)',
};
